import React, { useState, useEffect } from 'react';
import BackToTopButton from '../components/BackToTopButton';
import Footer from '../components/Footer';
import "../App.scss";
import upscale1 from '../images/Upscale-website-V1-thumb.jpg';
import upscale2 from '../images/Upscale-website-V2-thumb.jpg';
import upscale3 from '../images/Upscale-website-V3-thumb.jpg';
import cw1 from '../images/CW-website-V1-thumb.jpg';
import cw2 from '../images/CW-website-V2-thumb.jpg';
import cw3 from '../images/CW-website-V3-thumb.jpg';
import dw1 from '../images/DW-logo.jpg';
import dw2 from '../images/DW-storefront.jpg';
import dw3 from '../images/DW-merchant-tools.jpg';
import dw4 from '../images/DW-administration.jpg';
import cwc1 from '../images/cwc-new.jpg';
import cwc2 from '../images/cwc-old.jpg';
import cwc3 from '../images/cwc-mock.jpg';
import cwc4 from '../images/cwc-mock2.jpg';
import cwc5 from '../images/cwc-mock3.jpg';
import pets1 from '../images/pets-bopis-mock1.jpg';
import pets2 from '../images/pets-bopis-PDP.png';
import pets3 from '../images/pets-bopis-Cart.png';
import pets4 from '../images/pets-bopis-mock4.jpg';
import pets5 from '../images/pets-bopis-mock5.jpg';

const PortfolioText = ({ title, subtitle, description, children }) => (
  <div className="portfolio-text">
    <h3>{title}</h3>
    <p className="subtitle">{subtitle}</p>
    {description.map((para, index) => (
      <p key={index}>{para}</p>
    ))}
    {children}
  </div>
);

const PortfolioImages = ({ images, containerClassName, imagesContainerClass }) => {
  const [zoomedImage, setZoomedImage] = useState(null);

  const handleImageClick = (index) => {
    setZoomedImage((prevIndex) => (prevIndex === index ? null : index));
  };

  const handleClickOutside = (event) => {
    if (!event.target.closest(".portfolio-images img")) {
      setZoomedImage(null);
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  return (
    <div className={`portfolio-images ${imagesContainerClass || ""}`}>
      {images.map((img, index) => (
        <div className={`wrapper ${img.className} ${zoomedImage === index ? "zoom" : ""}`} key={index}>
          <img
            src={img.src}
            className={`${img.className} ${zoomedImage === index ? "zoom" : ""}`}
            alt={img.alt}
            onClick={(e) => {
              e.stopPropagation(); // Prevents the event from reaching the document listener
              handleImageClick(index);
            }}
          />
          {img.text && (
            <div className={`descriptive-text ${img.className}`}>
              <p>{img.text}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const PortfolioSection = ({ title, years, projects, imagesContainerClass }) => (
  <section className="portfolio-section">
    <h2>{title}</h2>
    <p className="years">({years})</p>
    {projects.map((project, index) => (
      <div class={project.containerClassName} key={index}>
        <PortfolioText title={project.title} subtitle={project.subtitle} description={project.description}>
          {project.link && (
            <p>
              <a href={project.link} target="_blank" rel="noopener noreferrer">
                {project.linkText}
              </a>
            </p>
          )}
        </PortfolioText>
        <PortfolioImages images={project.images} imagesContainerClass={project.imagesContainerClass} />
        <p className="click-zoom">(click to view)</p>
      </div>
    ))}
  </section>
);

const PortfolioPage = () => {
  const sections = [
    {
      title: "Freelance Work",
      years: "2009 - present",
      projects: [
        {
          containerClassName: "upscale",
          title: "Upscale Lawncare - Website Development",
          description: [
            "After completing my studies at ITT Technical Institute in March 2009, I began taking on freelance web development projects. One of my first projects was building the initial website for Upscale Lawncare, giving them their first online presence.",
            "The first version was a simple, static site built with HTML, CSS, and JavaScript. A few years later, I redesigned the site with an improved layout, enhanced visual appeal, and image carousels, creating a more polished user experience. In 2017, I launched the third iteration, migrating the site to WordPress. This version was fully responsive, offering a significantly improved experience on both desktop and mobile devices.",
            "As of 2019, I no longer manage Upscale Lawncare's website.",
          ],
          imagesContainerClass: "upscale-gallery",
          images: [
            { src: upscale1, className: "one", alt: "Upscale Lawncare v1", text: "Version 1 - 2009" },
            { src: upscale2, className: "two", alt: "Upscale Lawncare v2", text: "Version 2 - 2011" },
            { src: upscale3, className: "three", alt: "Upscale Lawncare v3", text: "Version 3 - 2017" },
          ],
        },
        {
          containerClassName: "cw",
          title: "CW Custom Homes - Website Development",
          description: [
            "In 2010, the owner of CW Custom Homes reached out to me to upgrade their company website. After discussing pain points with the existing site and identifying key goals, I developed a new version that significantly improved the user experience. The focus was on clean navigation and high-quality custom home image galleries, both of which were successfully implemented, resulting in a satisfied client.",
            "A year later, we launched a redesigned version featuring a refined color palette, updated navigation, a new logo, and refreshed homepage content. In 2019, we completed a full site redesign, migrating to WordPress to enhance functionality and implement a fully responsive design for better performance across devices.",
            "This WordPress site remains active today, and I continue to provide updates and maintenance as needed.",
          ],
          link: "https://cwcustomhomes.net/?utm_source=chad-espelage.com",
          linkText: "www.cwcustomhomes.net",
          imagesContainerClass: "cw-gallery",
          images: [
            { src: cw1, className: "one", alt: "CW Custom Homes v1", text: "Version 1 - 2010" },
            { src: cw2, className: "two", alt: "CW Custom Homes v2", text: "Version 2 - 2011" },
            { src: cw3, className: "three", alt: "CW Custom Homes v3", text: "Version 3 - 2019" },
          ],
        },
      ],
    },
    {
      title: "Demandware / Salesforce Commerce Cloud (SFCC B2C)",
      years: "2014 - present",
      projects: [
        {
          containerClassName: "demandware",
          title: "TradeGlobal",
          description: [
            "I was first introduced to Demandware in September 2014 when I joined TradeGlobal in Cincinnati, OH.",
            "During my first week, I was assigned to a full-site build for Coldwater Creek. As a newcomer to the platform, their site required a complete implementation, which our team was responsible for delivering.",
            "The project began with planning sessions, where we reviewed wireframes, Photoshop mockups, and identified both frontend and backend development tasks. Once the work was distributed among the team and a timeline was set, we followed a two-week sprint cycle to execute and complete the implementation."
          ],
          imagesContainerClass: "demandware",
          images: [
            { src: dw1, className: "one", alt: "Demandware" },
            { src: dw2, className: "two", alt: "Demandware - Site Genesis Storefront 2014", text: "Demandware - Site Genesis Storefront 2014" },
            { src: dw3, className: "three", alt: "Demandware - Merchant Tools Business Manager", text: "Demandware - Merchant Tools Business Manager" },
            { src: dw4, className: "four", alt: "Demandware - Administration Business Manager", text: "Demandware - Administration Business Manager" },
          ],
        },
        {
          containerClassName: "coldwater",
          title: "TradeGlobal - Coldwater Creek Site Build",
          subtitle: "(2014)",
          description: [
            "Led frontend development for Coldwater Creek's Demandware site build in 2014. During the build, I was responsible for transforming Photoshop mockups into fully functional web pages. This involved developing custom ISML templates using HTML, CSS, and JavaScript to create an engaging, responsive experience. Since the previous site lacked mobile compatibility, the new design incorporated a modern, mobile-friendly layout. I implemented responsive design techniques using CSS media queries and conducted cross-device testing with tools like BrowserStack to ensure consistency.",
            "Additionally, the client wanted much of the page content to be easily editable within Business Manager. To accommodate this, I configured templates to pull from various content assets and slots, enabling seamless updates without requiring code changes.",
            "Key Areas of Development:",
            <ol>
              <li>Header - Implemented promo slot, site logo, account/wishlist/shopping bag links, and site search.</li>
              <li>Navigation - Developed layouts for both mobile and desktop.</li>
              <li>Footer - Configured email signup, footer links, and social media icons.</li>
              <li>Homepage Content - Set up content assets for mobile and desktop promotional banners.</li>
              <li>Mini Cart - Developed responsive layouts for mobile and desktop.</li>
              <li>My Account - Built sections for address book, payment methods, order history, returns, and wishlist management.</li>
            </ol>
          ],
          imagesContainerClass: "coldwater",
          images: [
            { src: cwc1, className: "one", alt: "Coldwater Creek New Site", text: "New 2014 Site Design" },
            { src: cwc2, className: "two", alt: "Coldwater Creek Old Site", text: "Old Site Design" },
            { src: cwc3, className: "three", alt: "Coldwater Creek Header Mockup", text: "Coldwater Creek Header Mockup" },
            { src: cwc4, className: "four", alt: "Coldwater Creek Navigation Mockup", text: "Coldwater Creek Navigation Mockup" },
            { src: cwc5, className: "five", alt: "Coldwater Creek Minibag Mockup", text: "Coldwater Creek Minibag Mockup" },
          ],
        },
        {
          containerClassName: "pets-bopis",
          title: "Pet Supermarket - BOPIS Autoship Project",
          subtitle: "(2025)",
          description: [
            "As a contractor, I contributed to the implementation of an OrderGroove Buy Online, Pick Up In Store (BOPIS) AutoShip project. This feature enabled online shoppers to set up product subscriptions for in-store pickup at their preferred location.",
            "To support this functionality, I implemented a site preference toggle to enable or disable the feature, along with custom objects to manage attributes at both the product and store levels. Within various ISML templates, I added conditional logic to check these attributes. If all relevant conditions were met, the BOPIS AutoShip options were displayed on the Product Detail Pages (PDPs) and carried through to the Cart and Checkout process.",
            "Dynamic content visibility was managed using JavaScript and CSS, ensuring a seamless and intuitive user experience.",
            "Here are a few Figma mockups and Live site updates from the project:",
          ],
          imagesContainerClass: "pets-bopis",
          images: [
            { src: pets1, className: "one", alt: "Pet Supermarket Figma Design 1", text: "Figma - PDP options for Non-eligible BOPIS Autoship" },
            { src: pets2, className: "two", alt: "Pet Supermarket Updated PDP", text: "PDP Eligible BOPIS Autoship (desktop)" },
            { src: pets3, className: "three", alt: "Pet Supermarket BOPIS Autoship Cart", text: "BOPIS Autoship Cart layout" },
            { src: pets4, className: "four", alt: "Pet Supermarket Figma Design 4", text: "BOPIS Autoship Checkout -Figma Design" },
            { src: pets5, className: "five", alt: "Pet Supermarket Figma Design 5", text: "My Account - Figma Mockup" }
          ],
        },
      ],
    },
  ];

  return (
    <div className="page-container">
      <h1>Portfolio</h1>
      <p className="portfolio-desc">Showcase of various projects - both freelance & corporate.</p>
      <p className="portfolio-desc two">(<span>Work In Progress - More to come!</span>)</p>
      {sections.map((section, index) => (
        <PortfolioSection key={index} {...section} />
      ))}
      <BackToTopButton />
      <Footer />
    </div>
  );
};

export default PortfolioPage;
